/**
 * @format
 * @module ui/user-states
 */
import tgamConfig from "config/tgam";
import { getUserRole, isLoggedIn, getUserProfile } from "lib/identity/api";
import { getEntitlementClasses } from "lib/identity/entitlements";
import { loginAnchorClick } from "ui/login";
import { configureModal } from "ui/modal-manager";
import { registerAnchorClick } from "ui/register";
import visibilityByIdentity from "ui/visibility-by-identity";
import { removeAds, removeBody, removeElements, removeVideos } from "utils/dom-controller";
import { qsa, getProp, setProp } from "utils/utils";
import { isDesktop } from "utils/detect";
import { setUpModalView, getRegwallFrameUrl } from "lib/commercial/keytar/common/takeover";
import logger from "utils/logger";

const debugTag = "[USER-STATES]";

/**
 * @method init
 * @description initialize various user-state related UI things
 */
export default function init() {
  logger.info(`${debugTag} init()`);
  appendUserClassNames();
  visibilityByIdentity();
  replaceTextWithUserName(qsa(".js-text-replace--user-name"), "My account");
  initLoginButtons();
  initRegisterButtons();
}

/**
 * Display a modal regwall for desktop or an iframe takeover regwall for mobile.
 * @param {Object} [c={}] - collection of params
 * @param {String} [c.url] - for iframe src
 * @param {Number} [c.width=500] - width of modal
 * @param {Number} [c.height=820] - height of modal
 * @param {String} [c.uid="regwall"] - unique modal identity (within the webpage)
 * @param {String} [c.wallName="registerWall"] - wall name type
 * @param {String} [c.template] - darwin template markup
 * @see lib/commercial/keytar/anonymous/template.js
 * @see lib/commercial/keytar/promotion/template.js
 */
function showRegwallModal({
  url,
  width = 500,
  height = 820,
  uid = "regwall",
  wallName = "registerWall",
  template
} = {}) {
  const regwallUrl = getRegwallFrameUrl({ url, wallName });

  if (isDesktop()) {
    const elementsToRemove = [
      ".c-ad__message",
      ".c-byline",
      ".c-article-meta__creditlines",
      ".c-article-meta__timestamps",
      ".c-article-media__credit",
      ".c-article-media__caption",
      ".pb-f-article-content",
      ".c-timestamp",
      ".reading-time",
      ".c-image-container--lazy__wrapper",
      ".gc-chart",
      ".Clues", // cryptic crosswords page
      "table"
    ];

    // Display desktop modal regwall
    const modal = configureModal({
      uid,
      iframeUrl: regwallUrl,
      height,
      width,
      scrollable: true,
      topPaddingForCloseButton: false,
      protectedModal: true,
      onOpenModal: () => {
        removeElements(elementsToRemove);
        removeBody();
        removeAds();
        removeVideos();
      }
    });

    modal(); // display it now
  } else {
    // Display mobile iframe regwall
    const isInteractive = getProp(tgamConfig, "meta.isStoryInteractivePage", false);
    const elementsToRemove = isInteractive ? [] : ["top-furniture-area"];
    const classesToAdd = isInteractive ? [] : ["header-nav"];
    setUpModalView(wallName, template, ["iframe"], elementsToRemove, classesToAdd, regwallUrl);
  }
}

/**
 * @method initRegwallModals
 * @description initialize regwall modals used by Keytar
 */
export function initRegwallModals() {
  if (isLoggedIn()) {
    return;
  }
  setProp(window, "tgam.ui.regwall.show", showRegwallModal);
}

/**
 * @method appendUserClassNames
 * @description Add class name(s) to the HTML element to indicate the user's role and permissions
 */
function appendUserClassNames() {
  const role = getUserRole();
  if (role && role.name) {
    document.documentElement.classList.add(`u-user-role--${role.name.toLowerCase()}`);
  }
  // Iterate through all the available entitlements and add the class modifiers to the <html> tag
  getEntitlementClasses().forEach(entitlement => {
    document.documentElement.classList.add(`u-user-entitlement--${entitlement}`);
  });
}

/**
 * @method replaceTextWithUserName
 * @description Replaces each elements' text with the user's name
 * @param {Array<HTMLElement>} elements - An array of elements
 * @param {String} fallbackText - Fallback text for when the user's display name is unavailable
 */
export function replaceTextWithUserName(elements, fallbackText) {
  if (!isLoggedIn() || !Array.isArray(elements)) {
    return;
  }
  const userName = getProp(tgamConfig, "datalayer.identity.displayName", fallbackText);
  elements.forEach(el => (el.textContent = userName));
}

/**
 * @method replaceTextWithFullName
 * @description Replaces each elements' text with the user's first and last name
 * @param {Array<HTMLElement>} elements - An array of elements
 * @param {String} wrapper - tag element to wrap the user name
 * @param {String} klass - optional classes
 * @returns {Promise}
 */
export function replaceTextWithFullUserName(elements, wrapper, klass) {
  if (!isLoggedIn() || !Array.isArray(elements)) {
    return;
  }
  return getUserProfile()
    .then(profile => {
      const { user = {} } = profile;
      const { firstName = "", lastName = "" } = user;
      const userName = `${firstName} ${lastName}`;
      setProp(tgamConfig, "datalayer.identity.userFullName", { firstName, lastName });
      elements.forEach(el => {
        if (wrapper) {
          el.innerText = "";
          const title = document.createElement(wrapper);
          title.classList.add(klass);
          title.textContent = userName;
          el.appendChild(title);
        } else {
          el.textContent = userName;
        }
      });
    })
    .catch(error => {
      logger.info(`${debugTag} Invalid user's session`, error);
    });
}

/**
 * @method initLoginButtons
 * @description Attach a "login" click handler to all elements that have the specified class
 */
function initLoginButtons() {
  const loginButtons = qsa(".js-onclick--login");
  loginButtons.forEach(el => {
    el.addEventListener("click", loginAnchorClick);
  });
}

/**
 * @method initRegisterButtons
 * @description Attach a "register" click handler to all elements that have the specified class
 */
function initRegisterButtons() {
  const registerButtons = qsa(".js-onclick--register");
  registerButtons.forEach(el => {
    el.addEventListener("click", registerAnchorClick);
  });
}
